/**
 * The legal pages' side navigation - Figma 6377:88368.
 *
 * Used by `legal-list`, which the design draws as the same
 * two-column page with different content on the right: the page title over a stack of
 * links to the sibling legal pages, 227px wide inside a 440px column.
 *
 * A pill is 227 x 50 at `Radius/m`, 20px of padding, 14px centred text, on an 8px gap.
 * The current page is filled `green-400` with `green-900` text; the rest are outlined in
 * `grey-500`.
 *
 * **Which item is current is derived, not authored.** The editor writes the same list of
 * links on each legal page; the one whose URL matches the page being viewed highlights
 * itself. Asking an editor to tick "this is the current one" is asking them to keep six
 * copies of a list in step by hand, and to get it wrong once.
 *
 * **Both the link and the comparison go through `lib/links.ts`**, like every other CMS
 * URL. This file used to keep its own rule - "starts with `/` means internal" - so an
 * address an editor wrote as `regulacje-prawne/x`, without the slash, was treated as
 * another site: the pill pointed at a path relative to the current page and never
 * highlighted, on `/pl/regulacje-prawne/reprezentujacych-osobe-prawna`, 2026-11-04.
 */
import { useLocation } from 'react-router-dom';
import { AppLink } from '../../components/ui/AppLink';
import { resolveHref } from '../../lib/links';
import { useSite } from '../../lib/site-context';
import type { LinkItem } from '../../types/sections';

const PILL = 'flex items-center justify-center rounded-m p-5 text-center text-body-sm transition';

export default function LegalNav({ title, items }: { title?: string; items?: LinkItem[] }) {
  const { locale, t } = useSite();
  const { pathname } = useLocation();

  const isCurrent = (url: string) => {
    const target = resolveHref(locale, url);
    return target === pathname || `${target}/` === pathname || target === `${pathname}/`;
  };

  if (!title && !items?.length) return null;

  return (
    <div className="flex flex-col gap-8 lg:gap-[72px]">
      {title ? <h1 className="text-h3 font-normal text-ink-strong lg:text-h2">{title}</h1> : null}

      {items?.length ? (
        <nav aria-label={t('a11y.sectionNav')} className="flex w-full flex-col gap-2 lg:w-[227px]">
          {items.map((item, i) => {
            const current = isCurrent(item.url);
            return (
              <AppLink
                key={i}
                to={item.url}
                aria-current={current ? 'page' : undefined}
                className={`${PILL} ${
                  current
                    ? 'bg-green-400 text-green-900'
                    : 'border border-grey-500 text-ink hover:border-grey-700'
                }`}
              >
                {item.label}
              </AppLink>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
